import { CSSProperties, ReactNode, forwardRef } from 'react';

import clsx from 'clsx';

import { Badge } from './Badge';
import type { BadgeColor, BadgeSize, BadgeVariant } from './Badge';

export type StatusBadgePosition =
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left';
export type StatusBadgeOverlap = 'circular' | 'rectangular';

interface StatusBadgeProps {
  content?: ReactNode;
  status?:
    | 'online'
    | 'offline'
    | 'away'
    | 'error'
    | 'success'
    | 'warning'
    | 'info'
    | 'custom';
  color?: BadgeColor;
  size?: BadgeSize;
  variant?: BadgeVariant;
  position?: StatusBadgePosition;
  overlap?: StatusBadgeOverlap;
  inset?: number | string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

const positionStyle = (
  position: StatusBadgePosition,
  overlap: StatusBadgeOverlap,
  inset: number | string | undefined
): CSSProperties => {
  const base = typeof inset === 'number' ? `${inset}px` : inset || '0px';
  const style: CSSProperties = { position: 'absolute' };
  switch (position) {
    case 'top-right':
      style.top = base;
      style.right = base;
      break;
    case 'top-left':
      style.top = base;
      style.left = base;
      break;
    case 'bottom-right':
      style.bottom = base;
      style.right = base;
      break;
    case 'bottom-left':
      style.bottom = base;
      style.left = base;
      break;
  }
  if (overlap === 'circular') {
    style.transform = 'translate(25%, -25%)';
  }
  return style;
};

const statusColorMap: Record<string, BadgeColor> = {
  online: 'success',
  offline: 'gray',
  away: 'warning',
  error: 'error',
  success: 'success',
  warning: 'warning',
  info: 'info',
  custom: 'default',
};

export const StatusBadge = forwardRef<HTMLDivElement, StatusBadgeProps>(
  (
    {
      content,
      status,
      color,
      size = 'small',
      variant = 'solid',
      position = 'top-right',
      overlap = 'rectangular',
      inset,
      className,
      style,
      children,
      ...props
    },
    ref
  ) => {
    const badgeColor = color || (status ? statusColorMap[status] : 'default');
    return (
      <div
        className={clsx('status-badge-wrapper', className)}
        style={{ position: 'relative', display: 'inline-block', ...style }}
        ref={ref}
        {...props}
      >
        {children}
        <span
          className={clsx(
            'status-badge',
            `status-badge-size-${size}`,
            `status-badge-color-${color}`,
            `status-badge-variant-${variant}`,
            className
          )}
          style={positionStyle(position, overlap, inset)}
        >
          <Badge
            color={badgeColor}
            size={size}
            variant={variant}
            radius={overlap === 'circular' ? 'full' : 'medium'}
            pill={overlap === 'circular'}
          >
            {content}
          </Badge>
        </span>
      </div>
    );
  }
);
StatusBadge.displayName = 'StatusBadge';
